// One daily-report row, described as the UNION of every program's columns
// rather than six near-identical types. That is the honest shape for this
// module: the six column builders below all read from the same physical
// `*_dailys` row family, and each one names only the subset its program stores,
// so every field is optional by construction.
//
// The numeric columns are `number | string` because a value can arrive either
// from PostgREST (typed) or from an offline-queued form payload (text) — which
// is exactly why every arithmetic read below goes through parseFloat/parseInt.
export type DailyFeedEntry = {
  feed_name?: string | null;
  category?: string | null;
  qty?: number | string | null;
  unit?: string | null;
  lbs_as_fed?: number | string | null;
};

export type DailyMineralEntry = {name?: string | null; lbs?: number | string | null};

export type DailyReportRow = {
  id?: string | number | null;
  date?: string | null;
  team_member?: string | null;
  source?: string | null;
  comments?: string | null;
  issues?: string | null;
  photos?: readonly unknown[] | null;
  feeds?: readonly DailyFeedEntry[] | null;
  minerals?: readonly DailyMineralEntry[] | null;
  batch_label?: string | null;
  herd?: string | null;
  flock?: string | null;
  feed_type?: string | null;
  feed_lbs?: number | string | null;
  grit_lbs?: number | string | null;
  mortality_count?: number | string | null;
  mortality_reason?: string | null;
  group_moved?: boolean | null;
  waterer_checked?: boolean | null;
  water_checked?: boolean | null;
  waterers_working?: boolean | null;
  pig_count?: number | string | null;
  fence_voltage?: number | string | null;
  fence_voltage_kv?: number | string | null;
  nipple_drinker_moved?: boolean | null;
  nipple_drinker_working?: boolean | null;
  troughs_moved?: boolean | null;
  fence_walked?: boolean | null;
  layer_count?: number | string | null;
  group1_name?: string | null;
  group1_count?: number | string | null;
  group2_name?: string | null;
  group2_count?: number | string | null;
  group3_name?: string | null;
  group3_count?: number | string | null;
  group4_name?: string | null;
  group4_count?: number | string | null;
  daily_dozen_count?: number | string | null;
  dozens_on_hand?: number | string | null;
  dozens_allocated_coastal_rancher?: number | string | null;
};

export type DailyExportColumn = {header: string; value: (row: DailyReportRow) => string | number};

export function dailyExportYesNo(value: boolean | null | undefined): string {
  return value === false ? 'no' : 'yes';
}

export function dailyReportSourceLabel(value: string | null | undefined): string {
  return value === 'add_feed_webform' ? 'Add Feed' : 'Daily Report';
}

export function dailyPhotoCount(row: DailyReportRow): number {
  return Array.isArray(row.photos) ? row.photos.length : 0;
}

export function dailyFeedSummary(row: DailyReportRow): string {
  return Array.isArray(row.feeds)
    ? row.feeds
        .map((feed) => (feed.feed_name || '?') + (feed.qty != null ? ' ' + feed.qty + ' ' + (feed.unit || '') : ''))
        .join(', ')
    : '';
}

export function dailyMineralSummary(row: DailyReportRow): string {
  return Array.isArray(row.minerals)
    ? row.minerals
        .map((mineral) => {
          const parts = [mineral.name || '?'];
          if (mineral.lbs != null) parts.push(mineral.lbs + ' lb');
          return parts.join(' ');
        })
        .join(', ')
    : '';
}

// The `as string` assertions in the three reducers and in eggDailyTotalForRow
// below all say the same thing: parseFloat/parseInt perform ToString on their
// argument, so a numerically-typed stored value was always handled correctly.
// The assertion states that rather than inserting a String() call the runtime
// does not need.
export function dailyFeedLbsAsFed(row: DailyReportRow): string {
  return Array.isArray(row.feeds)
    ? row.feeds.reduce((sum, feed) => sum + (parseFloat(feed.lbs_as_fed as string) || 0), 0).toFixed(2)
    : '';
}

export function dailyMineralLbs(row: DailyReportRow): string {
  return Array.isArray(row.minerals)
    ? row.minerals.reduce((sum, mineral) => sum + (parseFloat(mineral.lbs as string) || 0), 0).toFixed(2)
    : '';
}

export function dailyHayBales(row: DailyReportRow): string {
  return Array.isArray(row.feeds)
    ? row.feeds
        .reduce(
          (sum, feed) =>
            sum + (feed.category === 'hay' && feed.unit === 'bale' ? parseFloat(feed.qty as string) || 0 : 0),
          0,
        )
        .toFixed(2)
    : '';
}

export function isDefaultSentinelComment(value: unknown): boolean {
  if (value == null) return true;
  const text = String(value).trim().toLowerCase();
  return text === '' || text === 'none' || text === '0' || text === 'n/a' || text === 'na' || text === '-';
}

export function eggDailyTotalForRow(row: DailyReportRow): number {
  return (
    (parseInt(row.group1_count as string) || 0) +
    (parseInt(row.group2_count as string) || 0) +
    (parseInt(row.group3_count as string) || 0) +
    (parseInt(row.group4_count as string) || 0)
  );
}

export function buildBroilerDailyExportColumns(): DailyExportColumn[] {
  return [
    {header: 'Date', value: (row) => row.date || ''},
    {header: 'Broiler group', value: (row) => row.batch_label || ''},
    {header: 'Team member', value: (row) => row.team_member || ''},
    {header: 'Source', value: (row) => dailyReportSourceLabel(row.source)},
    {header: 'Feed type', value: (row) => row.feed_type || ''},
    {header: 'Feed lbs', value: (row) => row.feed_lbs ?? ''},
    {header: 'Grit lbs', value: (row) => row.grit_lbs ?? ''},
    {header: 'Mortality count', value: (row) => row.mortality_count ?? ''},
    {header: 'Mortality reason', value: (row) => row.mortality_reason || ''},
    {header: 'Group moved', value: (row) => dailyExportYesNo(row.group_moved)},
    {header: 'Waterer checked', value: (row) => dailyExportYesNo(row.waterer_checked)},
    {header: 'Comments', value: (row) => row.comments || ''},
    {header: 'Photo count', value: dailyPhotoCount},
    {header: 'Record ID', value: (row) => row.id || ''},
  ];
}

export function buildPigDailyExportColumns(): DailyExportColumn[] {
  return [
    {header: 'Date', value: (row) => row.date || ''},
    {header: 'Pig group', value: (row) => row.batch_label || ''},
    {header: 'Team member', value: (row) => row.team_member || ''},
    {header: 'Source', value: (row) => dailyReportSourceLabel(row.source)},
    {header: 'Feed lbs', value: (row) => row.feed_lbs ?? ''},
    {header: 'Pig count', value: (row) => row.pig_count ?? ''},
    {header: 'Fence voltage', value: (row) => row.fence_voltage ?? ''},
    {header: 'Group moved', value: (row) => dailyExportYesNo(row.group_moved)},
    {header: 'Nipple drinker moved', value: (row) => dailyExportYesNo(row.nipple_drinker_moved)},
    {header: 'Nipple drinker working', value: (row) => dailyExportYesNo(row.nipple_drinker_working)},
    {header: 'Troughs moved', value: (row) => dailyExportYesNo(row.troughs_moved)},
    {header: 'Fence walked', value: (row) => dailyExportYesNo(row.fence_walked)},
    {header: 'Issues', value: (row) => row.issues || ''},
    {header: 'Photo count', value: dailyPhotoCount},
    {header: 'Record ID', value: (row) => row.id || ''},
  ];
}

export function buildCattleDailyExportColumns({
  herdLabels = {},
}: {herdLabels?: Record<string, string>} = {}): DailyExportColumn[] {
  return [
    {header: 'Date', value: (row) => row.date || ''},
    // `row.herd as string` keeps the existing lookup exactly: an absent herd
    // produced a miss and fell through to the two fallbacks that follow it.
    {header: 'Herd', value: (row) => herdLabels[row.herd as string] || row.herd || ''},
    {header: 'Team member', value: (row) => row.team_member || ''},
    {header: 'Source', value: (row) => dailyReportSourceLabel(row.source)},
    {header: 'Feed summary', value: dailyFeedSummary},
    {header: 'Feed lbs as fed', value: dailyFeedLbsAsFed},
    {header: 'Mineral summary', value: dailyMineralSummary},
    {header: 'Mineral lbs', value: dailyMineralLbs},
    {header: 'Fence voltage', value: (row) => row.fence_voltage ?? ''},
    {header: 'Water checked', value: (row) => dailyExportYesNo(row.water_checked)},
    {header: 'Mortality count', value: (row) => row.mortality_count ?? ''},
    {header: 'Mortality reason', value: (row) => row.mortality_reason || ''},
    {header: 'Issues', value: (row) => row.issues || ''},
    {header: 'Photo count', value: dailyPhotoCount},
    {header: 'Record ID', value: (row) => row.id || ''},
  ];
}

export function buildSheepDailyExportColumns({
  flockLabels = {},
  isSentinelComment = isDefaultSentinelComment,
}: {
  flockLabels?: Record<string, string>;
  isSentinelComment?: (value: unknown) => boolean;
} = {}): DailyExportColumn[] {
  return [
    {header: 'Date', value: (row) => row.date || ''},
    {header: 'Flock', value: (row) => flockLabels[row.flock as string] || row.flock || ''},
    {header: 'Team member', value: (row) => row.team_member || ''},
    {header: 'Source', value: (row) => dailyReportSourceLabel(row.source)},
    {header: 'Feed summary', value: dailyFeedSummary},
    {header: 'Feed lbs as fed', value: dailyFeedLbsAsFed},
    {header: 'Hay bales', value: dailyHayBales},
    {header: 'Mineral summary', value: dailyMineralSummary},
    {header: 'Mineral lbs', value: dailyMineralLbs},
    {header: 'Fence voltage kV', value: (row) => row.fence_voltage_kv ?? ''},
    {header: 'Waterers working', value: (row) => dailyExportYesNo(row.waterers_working)},
    {header: 'Mortality count', value: (row) => row.mortality_count ?? ''},
    {header: 'Comments', value: (row) => (isSentinelComment(row.comments) ? '' : row.comments || '')},
    {header: 'Photo count', value: dailyPhotoCount},
    {header: 'Record ID', value: (row) => row.id || ''},
  ];
}

export function buildLayerDailyExportColumns(): DailyExportColumn[] {
  return [
    {header: 'Date', value: (row) => row.date || ''},
    {header: 'Layer group', value: (row) => row.batch_label || ''},
    {header: 'Team member', value: (row) => row.team_member || ''},
    {header: 'Source', value: (row) => dailyReportSourceLabel(row.source)},
    {header: 'Feed type', value: (row) => row.feed_type || ''},
    {header: 'Feed lbs', value: (row) => row.feed_lbs ?? ''},
    {header: 'Grit lbs', value: (row) => row.grit_lbs ?? ''},
    {header: 'Layer count', value: (row) => row.layer_count ?? ''},
    {header: 'Group moved', value: (row) => dailyExportYesNo(row.group_moved)},
    {header: 'Waterer checked', value: (row) => dailyExportYesNo(row.waterer_checked)},
    {header: 'Mortality count', value: (row) => row.mortality_count ?? ''},
    {header: 'Mortality reason', value: (row) => row.mortality_reason || ''},
    {header: 'Comments', value: (row) => row.comments || ''},
    {header: 'Photo count', value: dailyPhotoCount},
    {header: 'Record ID', value: (row) => row.id || ''},
  ];
}

export function buildEggDailyExportColumns(): DailyExportColumn[] {
  return [
    {header: 'Date', value: (row) => row.date || ''},
    {header: 'Team member', value: (row) => row.team_member || ''},
    {header: 'Group 1 name', value: (row) => row.group1_name || ''},
    {header: 'Group 1 eggs', value: (row) => row.group1_count ?? ''},
    {header: 'Group 2 name', value: (row) => row.group2_name || ''},
    {header: 'Group 2 eggs', value: (row) => row.group2_count ?? ''},
    {header: 'Group 3 name', value: (row) => row.group3_name || ''},
    {header: 'Group 3 eggs', value: (row) => row.group3_count ?? ''},
    {header: 'Group 4 name', value: (row) => row.group4_name || ''},
    {header: 'Group 4 eggs', value: (row) => row.group4_count ?? ''},
    {header: 'Total eggs', value: eggDailyTotalForRow},
    {header: 'Daily dozens', value: (row) => row.daily_dozen_count ?? ''},
    {header: 'Dozens on hand', value: (row) => row.dozens_on_hand ?? ''},
    {
      header: 'Dozens Allocated for Coastal Rancher',
      value: (row) => row.dozens_allocated_coastal_rancher ?? '',
    },
    {header: 'Comments', value: (row) => row.comments || ''},
    {header: 'Record ID', value: (row) => row.id || ''},
  ];
}
